import { ActivationFlowConfig } from '@provision/entity-types';
import { gql, QueryHookOptions, useQuery } from '@provision/graphql-client';
import { useMemo } from 'react';
import { isNullish } from 'remeda';
import { ConditionInput, QueryVariables, Records } from 'src/graphql/types';

const GET_ACTIVATION_FLOW_CONFIG_QUERY = gql`
  query GetActivationFlowConfig($params: QueryParams) @entity(name: "ActivationFlowConfig") {
    getActivationFlowConfig(queryParams: $params) {
      records {
        id
        activationFlowConfigId
        partnerId
        brandId
        prices {
          partnerProduct {
            id
            partnerProductId
            displayName
            stripeProductId
            platformSlaId
            callForPricing
          }
          id
          partnerProductPriceId
          stripePriceId
          amountCents
          intervalUnit
          intervalCount
        }
      }
    }
  }
`;

interface GetActivationFlowConfig {
  getActivationFlowConfig: Records<ActivationFlowConfig>;
}

export interface OfferedProductPrice {
  partnerProductPriceId: string;
  stripePriceId: string;
  amountCents: number;
  intervalUnit: string;
  intervalCount: number;
}

export interface OfferedProduct {
  partnerProductId: string;
  displayName: string;
  stripeProductId: string;
  platformSlaId: string;
  callForPricing: boolean;
  prices: OfferedProductPrice[];
}

export const useActivationFlowConfig = (
  params: {
    partnerId: number | null;
    filters?: ConditionInput[];
  },
  options?: QueryHookOptions<GetActivationFlowConfig>,
) => {
  const { partnerId, filters = [] } = params ?? {};
  const { data, ...rest } = useQuery<GetActivationFlowConfig, QueryVariables>(
    GET_ACTIVATION_FLOW_CONFIG_QUERY,
    {
      ...options,
      skip: isNullish(partnerId) || options?.skip,
      variables: {
        params: {
          filters: [
            {
              attribute: 'partnerId',
              operator: '=',
              value: String(partnerId),
            },
            {
              attribute: 'isActive',
              operator: '=',
              value: 'true',
            },
            {
              attribute: 'isDefault',
              operator: '=',
              value: 'true',
            },
            ...filters,
          ],
        },
      },
    },
  );

  const config = data?.getActivationFlowConfig.records?.[0];

  return {
    config,
    ...rest,
  };
};

export function useOfferedProducts({
  partnerId,
  brandId,
}: {
  partnerId?: number | null;
  brandId?: string | null;
}) {
  const { config, loading, ...rest } = useActivationFlowConfig(
    {
      partnerId: partnerId!,
      filters: [
        {
          attribute: 'brandId',
          operator: '=',
          value: brandId!,
        },
      ],
    },
    { skip: !brandId || isNullish(partnerId) },
  );

  const offeredProducts = useMemo<OfferedProduct[]>(() => {
    if (!config) return [];

    const grouped = new Map<
      string,
      { product: NonNullable<(typeof config.prices)[number]['partnerProduct']>; prices: OfferedProductPrice[] }
    >();

    config.prices?.forEach((price) => {
      const pp = price.partnerProduct;
      if (!pp?.partnerProductId) return;

      if (!grouped.has(pp.partnerProductId)) {
        grouped.set(pp.partnerProductId, { product: pp, prices: [] });
      }

      grouped.get(pp.partnerProductId)!.prices.push({
        partnerProductPriceId: price.partnerProductPriceId,
        stripePriceId: price.stripePriceId,
        amountCents: price.amountCents,
        intervalUnit: price.intervalUnit,
        intervalCount: price.intervalCount,
      });
    });

    return Array.from(grouped.values()).map(({ product, prices }) => ({
      partnerProductId: product.partnerProductId,
      displayName: product.displayName,
      stripeProductId: product.stripeProductId,
      platformSlaId: product.platformSlaId,
      callForPricing: product.callForPricing,
      prices,
    }));
  }, [config]);

  return {
    offeredProducts,
    loading,
    ...rest,
  };
}
